<template>
  <tile>
    <div class="header">
      <p class="title">Loan Details</p>
    </div>
    <div class="loan-details-wrapper">
      <loader v-if="gettingData" />
      <div v-if="(loanSvcError || dataRetrievalError) && !gettingData">
        <error />
      </div>
      <template v-else>
        <div class="loan-details-section" ref="schoolConfirmationRef">
          <div class="loan-details-section-header">
            School Confirmation
            <a
              v-if="editable && applicationStatusId !== ApplicationStatusTypeIds.EVALUATION"
              class="edit-loan-config-link"
              href="#/"
              @click="openSchoolConfirmationModal"
              >Edit</a
            >
          </div>
          <div class="loan-details-section-content">
            <div class="info-section">
              <p class="info-title">School</p>
              <div class="info">
                <p>
                  {{ schoolName }}
                </p>
              </div>
            </div>
            <div class="info-section">
              <p class="info-title">Graduation Date</p>
              <div class="info">
                {{ graduationDate }}
              </div>
            </div>
            <div class="info-section">
              <p class="info-title">Academic Term(s)</p>
              <div class="info">
                {{ academicTerms }}
              </div>
            </div>
          </div>
        </div>
        <div class="loan-details-section" ref="loanTermsRef">
          <div class="loan-details-section-header">
            Loan Terms
            <a
              v-if="editable && applicationStatusId !== ApplicationStatusTypeIds.EVALUATION"
              class="edit-loan-config-link"
              href="#/"
              :disabled="editLoanTermsDisabled"
              @click="handleBorrowAmountModal"
              >Edit</a
            >
          </div>
          <div class="loan-details-section-content">
            <div class="info-section">
              <p class="info-title">Loan Amount</p>
              <div class="info">
                <p>
                  {{ loanAmount }}
                </p>
              </div>
            </div>
            <div class="info-section">
              <p class="info-title">Loan Amount Per Academic Term</p>
              <div class="info">
                {{ loanAmountPerTerm }}
              </div>
            </div>
            <div class="info-section">
              <p class="info-title">Repayment Type</p>
              <div class="info">
                {{ pelDetails?.repaymentTypeDisplay }}
              </div>
            </div>
            <div class="info-section">
              <p class="info-title">Rate Selection</p>
              <div class="info">
                {{ pelDetails?.rateSelectionDisplay }}
              </div>
            </div>
            <div class="info-section">
              <p class="info-title">Estimated In-School Monthly Payments</p>
              <div class="info">
                {{ pelDetails.monthlyInSchoolPaymentsDisplay }}
              </div>
            </div>
            <div class="info-section">
              <p class="info-title">Estimated Post-Grad Monthly Payments</p>
              <div class="info">
                {{ pelDetails.monthlyPostGradPaymentsDisplay }}
              </div>
            </div>
          </div>
        </div>
        <div class="tooltip-wrapper" style="height: 18px; z-index: 1">
          <tooltip-component class="xs-bottom-center">
            <template #toggle>
              <b><icon icon="info" /> Payment Estimates</b>
            </template>
            <div>
              Payment Estimates � See Your Approval Disclosure in your Legal Packet and your Final
              Disclosure after school certification is completed for more details.
              <b>
                <i>
                  The amount shown for In-School Monthly Payments is an average over the in-school
                  period.
                </i>
              </b>
              Actual payment amounts will vary based on the number of days in a month, the days
              between loan disbursements and any variable interest rate.
            </div>
          </tooltip-component>
        </div>
      </template>
    </div>
    <div class="loan-details-modals">
      <component :is="modalComponent" v-on="modalEventListeners" v-bind="modalProps"></component>
    </div>
  </tile>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useStore } from "vuex";
import Tile from "@/components/shared/Tile.vue";
import { ApplicationStatusTypeIds } from "@/config/constants";
import Loader from "@/components/shared/Loader.vue";
import { PelLoanConfigurationDetails } from "@/models/pel";
import { School } from "@/models/school";
import PelSchoolConfirmation from "@/components/pel-loan-configuration/PelSchoolConfirmation.vue";
import PelLoanConfigurationModal from "@/components/pel-loan-configuration/PelLoanConfigurationModal.vue";
import Error from "@/components/shared/Error.vue";
import ConfirmYourLoanDetailsModal from "@/components/pel-loan-configuration/ConfirmYourLoanDetailsModal.vue";
import TooltipComponent from "@/components/shared/TooltipComponent.vue";
import Icon from "@/components/shared/Icon.vue";
import useApplicationInfo from "@/composables/application-info/useApplicationInfo";

interface LoanDetails {
  academicTerms: string;
  graduationDate?: Date;
  graduationDateDisplay: string;
  requestedAmount?: number;
  requestedAmountDisplay: string;
  requestedAmountPerTerm?: number;
  requestedAmountPerTermDisplay: string;
  school?: School;
  schoolName: string;
}

const store = useStore();

const currentlyActiveModal = computed(() => store.getters["applicationInfoModule/ModalComponent"]);

const loanConfigurationModule = computed(() => store.state.loanConfigurationModule);

// Reactive
const info = ref({} as LoanDetails);

const {
  appData,
  applicationStatusId,
  closeSchoolConfirmationModal,
  dataRetrievalError,
  details,
  editable,
  editLoanTermsDisabled,
  error,
  initialDraw,
  loan,
  loanSvcError,
  requestedAmount,
  requestedAmountPerTerm,
  schoolName,
  getApplicationDetails,
  gettingData,
  handleSchoolConfirmation,
  updatingApp,
} = useApplicationInfo();

const pelDetails = computed<PelLoanConfigurationDetails>(() => details.value);

const loanAmount = computed<string>(() => {
  return requestedAmount.value;
});

const loanAmountPerTerm = computed<string>(() => {
  return requestedAmountPerTerm.value;
});

const graduationDate = computed<string>(() => {
  return pelDetails.value.graduationDateDisplay;
});

const academicTerms = computed<string>(() => {
  return pelDetails.value.academicTerms;
});

const modalComponent = computed(() => {
  return getModalComponent();
});

const modalEventListeners = computed(() => {
  switch (modalComponent.value) {
    case PelSchoolConfirmation:
      return {
        complete: handleSchoolConfirmation,
        close: closeSchoolConfirmationModal,
      };
    case PelLoanConfigurationModal:
      return {
        configurationComplete: handleLoanConfigurationComplete,
        close: closeLoanConfigModal,
      };
    case ConfirmYourLoanDetailsModal:
      return {
        openLoanConfigurationModal: handleBorrowAmountModal,
        openSchoolConfirmationModal: openSchoolConfirmationModal,
      };
    default:
      return {};
  }
});

const modalProps = computed(() => {
  switch (modalComponent.value) {
    case PelSchoolConfirmation:
      return {
        term: academicTerms.value,
        loading: updatingApp.value,
      };

    default:
      return {};
  }
});
// Methods

function getModalComponent() {
  switch (currentlyActiveModal.value) {
    case "ConfirmYourLoanDetails":
      return ConfirmYourLoanDetailsModal;
    case "PelLoanConfigurationModal":
      return PelLoanConfigurationModal;
    case "PelSchoolConfirmationModal":
      return PelSchoolConfirmation;
    default:
      return;
  }
}

async function handleLoanConfigurationComplete() {
  await closeLoanConfigModal();
}

async function closeLoanConfigModal() {
  await getApplicationDetails();
  store.dispatch("applicationInfoModule/closeModal", "PelLoanConfigurationModal");
}

function openSchoolConfirmationModal() {
  store.dispatch("applicationInfoModule/openModal", "PelSchoolConfirmationModal");
}

function handleBorrowAmountModal() {
  if (!editLoanTermsDisabled.value) {
    store.dispatch("applicationInfoModule/openModal", "PelLoanConfigurationModal");
  } else {
    store.dispatch("modal/openModal", {
      title: "Alert",
      description: "School confirmation details must be completed prior to selecting Loan Terms.",
      allowBackgroundScroll: false,
			buttonsPosition: "right",
    });
  }
}

onMounted(async () => {
  await getApplicationDetails();
});

defineExpose({
  appData,
  initialDraw,
  loan,
});
</script>

<style scoped>
p {
  margin: 0;
  padding: 0;
}

.header {
  font-weight: 700;
  font-size: 1.5625rem; /* 25px */
}

.loan-details-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.loan-details-wrapper .loan-details-section-header {
  font-weight: 700;
  font-size: 1.25rem; /* 20px */
}

.loan-details-wrapper .loan-details-section-content .info-section .info-title {
  font-weight: 600;
  font-size: 0.875;
}

.loan-details-wrapper .loan-details-section-header a.edit-loan-config-link {
  text-decoration: underline;
  font-weight: var(--font-weight-medium);
  font-size: 0.875rem;
  color: #000000;
}

.loan-details-wrapper .loan-details-section-header a.edit-loan-config-link[disabled="true"] {
  color: #c3c3c3;
  cursor: not-allowed;
}

.loan-details-wrapper .loan-details-section-content .info-section .info-title .info {
  font-size: 0.875;
}

.loan-details-wrapper .loan-details-section-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
